import { useState, useEffect, useCallback, useRef } from "react";

const INTERVAL = 5000;

function Stars({ rating = 0, total = 5 }) {
  const full = Math.round(rating);
  return (
    <div className="flex gap-[3px]" aria-label={`Rated ${rating} out of ${total}`}>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`text-sm ${i < full ? "text-red-600" : "text-black/15"}`}
        >
          ★
        </span>
      ))}
    </div>
  );
}

export default function FeaturedProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);

  const elapsedRef = useRef(0);
  const touchStartX = useRef(null);

  // ── FETCH ─────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch("https://code.gurusport.in/api/site-banner");
        const data = await res.json();
        if (data?.featured_products?.data) setProducts(data.featured_products.data);
      } catch (err) {
        console.error("Error fetching featured products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const total = products.length;

  const goTo = useCallback(
    (index) => {
      if (!total) return;
      elapsedRef.current = 0;
      setProgress(0);
      setCurrent(((index % total) + total) % total);
    },
    [total]
  );

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  // ── AUTOPLAY ──────────────────────────────────────────────────────
  // One rAF loop drives both the progress bar and the advance, so the two
  // can never drift apart. Pausing keeps the elapsed time and resumes from it.
  useEffect(() => {
    if (!total || paused) return;

    let frame;
    const start = performance.now() - elapsedRef.current;

    const tick = (now) => {
      const elapsed = now - start;
      elapsedRef.current = elapsed;

      const pct = Math.min((elapsed / INTERVAL) * 100, 100);
      setProgress(pct);

      if (pct >= 100) {
        elapsedRef.current = 0;
        setProgress(0);
        setCurrent((c) => (c + 1) % total);
        return;
      }

      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [current, total, paused]);

  // ── KEYBOARD ──────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [next, prev]);

  // ── SWIPE ─────────────────────────────────────────────────────────
  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) diff < 0 ? next() : prev();
    touchStartX.current = null;
  };

  if (loading || !total) {
    return (
      <div className="py-20 text-center text-xs tracking-[0.3em] uppercase text-black/40">
        Loading featured products<span className="animate-pulse">...</span>
      </div>
    );
  }

  return (
    <section className="bg-white text-black py-16 lg:py-20 px-4">
      {/* HEADER */}
      <div className="max-w-7xl mx-auto mb-10">
        <span className="text-red-600 uppercase tracking-[4px] text-xs font-semibold">
          Hand Picked
        </span>

        <h2 className="text-black text-4xl md:text-5xl font-black uppercase mt-4">
          Featured Gear
        </h2>

        <div className="flex items-center gap-2 mt-5">
          <div className="w-20 h-[2px] bg-red-600" />
          <div className="w-2 h-[2px] bg-black" />
        </div>
      </div>

      {/* WRAPPER */}
      <div
        className="relative max-w-7xl mx-auto"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* SLIDER */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-[0_10px_40px_rgba(0,0,0,0.06)]">
          {/* PROGRESS */}
          <div className="h-[3px] w-full bg-black/10">
            <div className="h-full bg-red-600" style={{ width: `${progress}%` }} />
          </div>

          <div
            className="flex transition-transform duration-700 ease-in-out"
            style={{ transform: `translateX(-${current * 100}%)` }}
          >
            {products.map((product, i) => {
              const imgSrc =
                product.default_image?.length > 0
                  ? product.default_image
                  : "/images/placeholder.png";

              const rawDesc = (product.description || "")
                .replace(/<[^>]*>/g, "")
                .substring(0, 140);

              const hasDiscount =
                product.price && product.price !== product.selling_price;

              const off = hasDiscount
                ? Math.round(
                    ((product.price - product.selling_price) / product.price) * 100
                  )
                : 0;

              const rating = product.average_rating ?? product.rating ?? 0;

              return (
                <div
                  key={product.id ?? i}
                  className="min-w-full grid grid-cols-1 md:grid-cols-2 bg-white"
                  aria-hidden={i !== current}
                >
                  {/* IMAGE */}
                  <div className="relative bg-gradient-to-br from-gray-50 via-white to-gray-100 h-[300px] sm:h-[380px] md:h-[560px] overflow-hidden group">
                    <span className="absolute top-5 left-5 z-10 bg-red-600 text-white text-[10px] font-black tracking-[0.2em] uppercase px-4 py-2 rounded-full shadow-md shadow-red-600/25">
                      Top Pick
                    </span>

                    {off > 0 && (
                      <span className="absolute top-5 right-5 z-10 bg-black text-white text-[10px] font-black tracking-[0.15em] uppercase px-3 py-2 rounded-full">
                        {off}% off
                      </span>
                    )}

                    <a href={`/${product.slug}`} className="block w-full h-full">
                      <img
                        src={imgSrc}
                        alt={product.name}
                        className="w-full h-full object-contain p-6 md:p-10 transition-transform duration-[1200ms] ease-out group-hover:scale-105 motion-reduce:transition-none"
                      />
                    </a>
                  </div>

                  {/* CONTENT */}
                  <div className="relative flex items-center bg-white border-t md:border-t-0 md:border-l border-gray-200">
                    <div className="absolute left-0 top-[15%] h-[70%] w-[3px] bg-red-600 rounded-r-full hidden md:block" />

                    <div className="px-6 sm:px-8 md:px-12 py-10 md:py-0 w-full">
                      {/* COUNT */}
                      <div className="flex items-center gap-3 mb-5">
                        <span className="text-[10px] font-semibold text-gray-500 tracking-[0.25em] uppercase tabular-nums">
                          {String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                        </span>
                        <div className="w-10 h-px bg-black/20" />
                      </div>

                      {/* TITLE */}
                      <h3 className="text-2xl md:text-3xl font-black uppercase leading-tight text-black mb-3">
                        {product.name}
                      </h3>

                      {rating > 0 && (
                        <div className="mb-4">
                          <Stars rating={rating} />
                        </div>
                      )}

                      {/* DESCRIPTION */}
                      <p className="text-sm md:text-base text-gray-600 leading-7 mb-7 max-w-md">
                        {rawDesc}
                      </p>

                      {/* PRICE */}
                      <div className="flex flex-wrap items-baseline gap-3 mb-8">
                        <span className="text-2xl md:text-3xl font-black text-black">
                          ₹{product.selling_price}
                        </span>

                        {hasDiscount && (
                          <span className="text-base text-gray-400 line-through">
                            ₹{product.price}
                          </span>
                        )}
                      </div>

                      {/* BUTTONS */}
                      <div className="flex flex-wrap items-center gap-5">
                        <a
                          href={`/${product.slug}`}
                          tabIndex={i === current ? 0 : -1}
                          className="inline-flex items-center bg-black text-white text-xs font-black tracking-[0.18em] uppercase px-7 py-4 rounded-xl hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-colors duration-300"
                        >
                          Shop Now
                        </a>

                        <a
                          href={`/${product.slug}`}
                          tabIndex={i === current ? 0 : -1}
                          className="text-xs font-bold tracking-[0.15em] uppercase border-b-2 border-red-600 pb-1 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-colors"
                        >
                          Full details
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* BOTTOM NAV */}
        <div className="flex items-center justify-between mt-6 flex-wrap gap-4">
          {/* DOTS */}
          <div className="flex gap-2 items-center">
            {products.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === current}
                className={`h-[4px] rounded-full transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-red-600 ${
                  i === current ? "w-10 bg-red-600" : "w-4 bg-black/20 hover:bg-black/40"
                }`}
              />
            ))}
          </div>

          {/* COUNTER */}
          <div className="text-xs font-semibold text-gray-500 tracking-[0.2em] tabular-nums">
            <span className="text-black text-xl font-black">
              {String(current + 1).padStart(2, "0")}
            </span>{" "}
            / {String(total).padStart(2, "0")}
          </div>

          {/* ARROWS */}
          <div className="flex gap-2">
            <button
              onClick={prev}
              aria-label="Previous product"
              className="w-12 h-12 rounded-xl bg-white border border-gray-200 text-black text-lg font-bold flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-colors duration-300"
            >
              ←
            </button>

            <button
              onClick={next}
              aria-label="Next product"
              className="w-12 h-12 rounded-xl bg-white border border-gray-200 text-black text-lg font-bold flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-colors duration-300"
            >
              →
            </button>
          </div>
        </div>

        {/* HINT */}
        <div className="flex items-center justify-center gap-2 mt-6 text-[10px] text-gray-500 tracking-wider">
          <span className="border border-gray-300 px-2 py-[2px] rounded">←</span>
          Use arrow keys to browse
          <span className="border border-gray-300 px-2 py-[2px] rounded">→</span>
        </div>
      </div>
    </section>
  );
}